// AGC: "User activity" — daily active users line chart.
import { useEffect, useState } from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import { InsightCard } from './InsightCard'
import {
  getActiveUsersPerDay,
  type DateRange,
  type ActiveUserPoint,
} from '../../../services/analyticsQueryService'
import { useTranslation } from '../../../hooks/useTranslation'

interface EngagementSectionProps {
  range: DateRange
}

export function EngagementSection({ range }: EngagementSectionProps): JSX.Element {
  const [points, setPoints] = useState<ActiveUserPoint[] | null>(null)
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()

  useEffect(() => {
    // AGC: Cancel flag prevents setState on unmounted component
    let cancelled = false
    setLoading(true)
    getActiveUsersPerDay(range)
      .then((p) => { if (!cancelled) setPoints(p) })
      .catch((err) => { console.warn('[analytics-dashboard] engagement failed', err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [range])

  // AGC: True when all data points have zero active users (nothing to show)
  const allZero = points?.every((p) => p.activeUsers === 0) ?? false

  return (
    <InsightCard
      title={t('admin.analytics.card.activeUsersPerDay')}
      helpDescription="How many unique users were active on each day in the period."
      helpGoodSignal="A rising line means growing engagement."
      helpBadSignal="A flat or declining line with steady installs means churn."
      helpAction="If the line is spiky, check what changed between peak days."
    >
      {loading || !points ? (
        <div className="h-64 bg-slate-100 dark:bg-white/[0.04] animate-pulse rounded" />
      ) : allZero ? (
        <div className="text-sm text-slate-500 dark:text-slate-400 h-64 flex items-center justify-center">
          {t('admin.analytics.empty.noActivity')}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={points} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="activeUsers"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </InsightCard>
  )
}
